import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import type { ManualGradeCredit } from '@/types/coaching'

export type PartialCreditGrade = ManualGradeCredit

interface Props {
  value?: PartialCreditGrade | null
  onChange: (grade: PartialCreditGrade) => void
  disabled?: boolean
  /** false면 부분정답(△)을 제외한 O/X 2단 토글로 렌더한다. 기본 true(워크북·빌드업 현행). */
  allowPartial?: boolean
  /** role=group의 aria-label. 기본은 기존 부분정답 라벨. */
  ariaLabel?: string
}

const OPTIONS: ReadonlyArray<{
  grade: PartialCreditGrade
  testId: string
  symbol: string
  labelKey: string
  activeClass: string
}> = [
  {
    grade: 1,
    testId: 'grade-correct',
    symbol: 'O',
    labelKey: 'override.toCorrect',
    activeClass: 'bg-lc-mint-900 text-white shadow-[0_2px_0_0_var(--color-lc-mint-dark)]',
  },
  {
    grade: 0.5,
    testId: 'grade-partial',
    symbol: '△',
    labelKey: 'override.toPartial',
    activeClass: 'bg-lc-orange-900 text-white shadow-[0_2px_0_0_var(--color-lc-orange-dark)]',
  },
  {
    grade: 0,
    testId: 'grade-incorrect',
    symbol: 'X',
    labelKey: 'override.toIncorrect',
    activeClass: 'bg-lc-red-900 text-white shadow-[0_2px_0_0_var(--color-lc-red-dark)]',
  },
]

export function PartialCreditGradeToggle({
  value,
  onChange,
  disabled,
  allowPartial = true,
  ariaLabel,
}: Props) {
  const { t } = useTranslation('coaching')
  const options = allowPartial ? OPTIONS : OPTIONS.filter((option) => option.grade !== 0.5)

  return (
    <div
      data-testid="partial-credit-grade-toggle"
      role="group"
      aria-label={ariaLabel ?? t('override.partialCreditAriaLabel')}
      className={clsx(
        'grid w-full max-w-[420px] gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1',
        allowPartial ? 'grid-cols-3' : 'grid-cols-2',
      )}
    >
      {options.map((option) => (
        <button
          key={option.grade}
          type="button"
          data-testid={option.testId}
          disabled={disabled}
          aria-pressed={value === option.grade}
          onClick={() => onChange(option.grade)}
          className={clsx(
            'inline-flex min-h-12 min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1.5 py-2 text-sm font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1',
            value === option.grade
              ? option.activeClass
              : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <span aria-hidden="true" className="text-[15px] font-bold">{option.symbol}</span>
          <span className="text-center leading-tight">{t(option.labelKey)}</span>
        </button>
      ))}
    </div>
  )
}
